import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import momentPlugin from '@fullcalendar/moment';
import { useService } from 'contexts/ServiceContextProvider';
import { useParams } from 'react-router-dom';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import moment from 'moment';

import { DateTime } from 'luxon';
import { Typography, Avatar, Badge, Box, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import {ConvertToMyTimeZone, convertToUTCISO} from 'components/Custom/TimezoneConvertion.jsx';
import TourIcon from '@mui/icons-material/Tour';
import EventDetailsModal from './Events/EventsDetailsModal';
import { useTranslation } from "react-i18next";
import '../../css/components/Calendar/CalendarComponent.css';
import '../../css/main.css';
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';

/* eslint-disable react-hooks/exhaustive-deps */
const CalendarComponent = () => {
  useEffect(() => {
    document.title = t('titles.calendar');
  }, []);
  const { t } = useTranslation();

  const workspaceContext = useWorkspaces();

  const [events, setEvents] = useState([]);
  const { fetchWorkspaceCalendar, getUserTimeZone, updateWorkspaceEvent, deleteWorkspaceEvent, getUserSettings } = useService();
  const { workspaceId } = useParams();
  const [myUserTimezone, setMyUserTimezone] = useState(null);
  const [showHolidayEvents, setShowHolidayEvents] = useState(true);

  const [showNormalEvents, setShowNormalEvents] = useState(true);
  const [myUserPerms, setMyUserPerms] = useState(null);
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const calendarRef = useRef(null);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const [, setDayHeaderFormat] = useState(window.innerWidth <= 600 ? { weekday: 'short' } : { weekday: 'long' });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const [calendarStartDay, setCalendarStartDay] = useState(() => {
    return Number(localStorage.getItem('calendar_start_day')) || 0;
  });



  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 600) {
        setIsMobile(true);
        setDayHeaderFormat({ weekday: 'short' });
      } else {
        setIsMobile(false);
        setDayHeaderFormat({ weekday: 'long' });
      }

      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const currentView = calendarApi.view.type;


        if (currentView.startsWith('timeGridWeek')) {
          if (window.innerWidth <= 400 && currentView !== 'timeGridWeekTwo') {
            calendarApi.changeView('timeGridWeekTwo');
          } else if (window.innerWidth <= 500 && window.innerWidth > 400 && currentView !== 'timeGridWeekThree') {
            calendarApi.changeView('timeGridWeekThree');
          } else if (window.innerWidth <= 600 && window.innerWidth > 500 && currentView !== 'timeGridWeekFour') {
            calendarApi.changeView('timeGridWeekFour');
          } else if (window.innerWidth <= 700 && window.innerWidth > 600 && currentView !== 'timeGridWeekFive') {
            calendarApi.changeView('timeGridWeekFive');
          } else if (window.innerWidth <= 800 && window.innerWidth > 700 && currentView !== 'timeGridWeekSix') {
            calendarApi.changeView('timeGridWeekSix');
          } else if (window.innerWidth > 800 && currentView !== 'timeGridWeek') {
            calendarApi.changeView('timeGridWeek');
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);

    // Trigger the resize handler on component mount to set the initial view correctly
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);




  const userColors = new Map();

  const generateColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  const getCountryFlag = (countryCode) => {
    return countryCode?.toLowerCase(); // Converts country code like PT to pt for the flag URL
  };

  const getUserColor = (userName) => {
    if (!userColors.has(userName)) {
      const colorIndex = userColors.size;
      userColors.set(userName, generateColor(colorIndex));
    }
    return userColors.get(userName);
  };

  const handleEventDelete = async (event_id, workspace_id) => {
    await deleteWorkspaceEvent(event_id, workspace_id)
    setEvents(prevEvents => prevEvents.filter(event => event.id !== event_id))
    setDialogOpen(false);
  }

  const handleEventSave = async (editableEvent) => {
    try {
      const updatedEvent = {
        ...editableEvent,
        collaborators: editableEvent.collaborators.map(c => c.id),
        date_start: editableEvent.all_day_event
            ? DateTime.fromISO(editableEvent.date_start).toUTC().toISODate()
            : convertToUTCISO(editableEvent.date_start, myUserTimezone),
        date_end: editableEvent.all_day_event
            ? DateTime.fromISO(editableEvent.date_end).plus({ days: 1 }).toUTC().toISODate()
            : convertToUTCISO(editableEvent.date_end, myUserTimezone),
      };

      await updateWorkspaceEvent(updatedEvent, workspaceId, updatedEvent.id);

      setEvents(prevEvents => {
        return prevEvents.map(event => {
          if (event.id === updatedEvent.id) {
            return {
              ...event,
              title: editableEvent.title,
              creator: editableEvent.creator,
              start: editableEvent.all_day_event
                  ? DateTime.fromISO(editableEvent.date_start).toUTC().toISODate()
                  : DateTime.fromISO(editableEvent.date_start, { zone: myUserTimezone }).toUTC().toISO(),
              end: editableEvent.all_day_event
                  ? DateTime.fromISO(editableEvent.date_end).plus({ days: 1 }).toUTC().toISODate()
                  : DateTime.fromISO(editableEvent.date_end, { zone: myUserTimezone }).toUTC().toISO(),
              allDay: editableEvent.all_day_event,
              backgroundColor: editableEvent.colorCode,
              extendedProps: {
                ...event.extendedProps,
                description: editableEvent.description,
                collaborators: editableEvent.collaborators,
                visibility: editableEvent.visibility
              }
            };
          }
          return event;
        });
      });

      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to update event:", error);
    }
  };



  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps.type === 'normal') {
      let startUTC = DateTime.fromISO(clickInfo.event.startStr, { zone: 'utc' }).toISO();
      let endUTC = DateTime.fromISO(clickInfo.event.endStr, { zone: 'utc' }).toISO();


      setSelectedEvent({
        ...clickInfo.event,
        start: startUTC,
        end: endUTC,
        allDay: clickInfo.event.allDay,
        creator: clickInfo.event.extendedProps.creator,
        backgroundColor: clickInfo.event.backgroundColor,
        title: clickInfo.event.title,
        id: clickInfo.event.id,
        extendedProps: { ...clickInfo.event.extendedProps }
      });

      setDialogOpen(true);
    }
  };



  const handleMobileEventClick = (event) => {
    if (event.extendedProps.type === 'normal') {
      let startUTC = DateTime.fromISO(event.startStr, { zone: 'utc' }).toISO();
      let endUTC = DateTime.fromISO(event.endStr, { zone: 'utc' }).toISO();


      setSelectedEvent({
        ...event,
        start: startUTC,
        end: endUTC,
        allDay: event.allDay,
        backgroundColor: event.backgroundColor,
        title: event.title,
        id: event.id,
        extendedProps: { ...event.extendedProps }
      });

      setDialogOpen(true);
    }
  }


  useEffect(() => {
    const initializeCalendar = async () => {
      try {
        //const userId = workspaceContext.user?.id;
        if (workspaceId) {
          const [data, timezoneResponse, settings] = await Promise.all([
            fetchWorkspaceCalendar(workspaceId),
            getUserTimeZone(),
            getUserSettings(workspaceId)
          ]);
          setMyUserTimezone(timezoneResponse.timezone);

          if (settings && settings.calendar_start_day !== undefined) {
            const startDay = Number(settings.calendar_start_day);
            setCalendarStartDay(startDay);
            localStorage.setItem('calendar_start_day', startDay);
          }
          const users = data.working_hours.map(wh => wh.user_info);

          const holidayEvents = data.holidays.map((holiday) => {
            const affectedUsers = users.filter(user =>
                holiday.country === user.location?.split('(')[1]?.replace(')', '')
            );

            return {
              id: holiday.id,
              type: 'holiday',
              start: holiday.start,
              allDay: holiday.allDay,
              name: holiday.name,
              local_name: holiday.local_name,
              country: holiday.country,
              affects_all_country: holiday.affects_all_country,
              sub_region: holiday.sub_region,
              source: holiday.source,
              backgroundColor: '#ffeeab',
              extendedProps: {
                ...holiday.extendedProps,
                affectedUsers,
              },
            };
          });
          const workingHourEvents = generateWorkingHourEvents(data.working_hours, timezoneResponse);
          const normalEvents = generateNormalEvents(data.events, timezoneResponse.timezone);

          setEvents([...holidayEvents, ...workingHourEvents, ...normalEvents]);

        }
      } catch (error) {
        console.error('Error fetching events or timezone:', error);
      }
    };

    initializeCalendar();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0) {
      const userPerms = workspaceContext.getLoggedUserPermissions();
      setMyUserPerms(userPerms);

    }
  }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

  const openEventModal = (eventsOnDay) => {
    setSelectedEvents(eventsOnDay);
    setMobileDialogOpen(true);
  };

  const handleClose = () => {
    setMobileDialogOpen(false);
    setSelectedEvents([]);
  };

  // Ensure all times are converted and displayed consistently
  const generateWorkingHourEvents = (workingHoursData, loggedUserTimezone) => {
    const events = [];
    const dayMap = {
      Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 0,
    };

    workingHoursData.forEach((workingHours) => {
      const { start, end, day, timezone, user_info } = workingHours;
      const userName = user_info.full_name;
      const userColor = getUserColor(userName);
      const weekday = dayMap[day];

      if (weekday !== undefined) {
        // Convert start and end times from user's working hours to myUserTimezone
        const startDateTime = DateTime.fromObject({ weekday, hour: parseInt(start.split(':')[0]), minute: parseInt(start.split(':')[1]) }, { zone: timezone })
            .setZone(loggedUserTimezone.timezone, { keepLocalTime: true });
        const endDateTime = DateTime.fromObject({ weekday, hour: parseInt(end.split(':')[0]), minute: parseInt(end.split(':')[1]) }, { zone: timezone })
            .setZone(loggedUserTimezone.timezone, { keepLocalTime: true });


        events.push({
          type: 'working_hours',
          title: `${userName}'s Working Hours`,
          start: startDateTime.toISO(),
          end: endDateTime.toISO(),
          allDay: true,
          backgroundColor: userColor,
          textColor: '#000000',
          extendedProps: { user: { userName, userAvatar: user_info.image } },

        });
      }
    });

    return events;
  };

  // Convert normal events to myUserTimezone consistently
  const generateNormalEvents = (normalEventsData, timezone) => {
    return normalEventsData.map((event) => {
      let startDateTime, endDateTime;
      if (event.all_day_event) {
        // All-day events should use date-only strings to avoid timezone shift issues
        startDateTime = DateTime.fromISO(event.date_start, { zone: 'utc' }).toISODate();
        endDateTime = DateTime.fromISO(event.date_end, { zone: 'utc' }).toISODate();

      } else {
        // Non-all-day events should use full ISO strings in UTC
        startDateTime = DateTime.fromISO(event.date_start, { zone: timezone }).toISO();
        endDateTime = DateTime.fromISO(event.date_end, { zone: timezone }).toISO();
      }

      const generatedEvent = {
        type: 'normal',
        creator: event.creator,
        id: event.id,
        title: event.title,
        start: startDateTime,
        end: endDateTime,
        allDay: event.all_day_event,
        backgroundColor: event.color_code,
        textColor: '#fff',
        extendedProps: { description: event.description, collaborators: event.collaborators, visibility: event.visibility },
      };

      return generatedEvent;
    });
  };





  const filteredEvents = events.filter((event) => {
    if (event.type === 'working_hours') return false;
    if (!showHolidayEvents && event.type === 'holiday') return false;
    if (!showNormalEvents && event.type === 'normal') return false;
    return true;
  });



  const renderEventContent = (eventInfo) => {
    const { users, collaborators, affectedUsers, local_name, name, country } = eventInfo.event.extendedProps;

    if (users) {
      const distinctUsers = [...new Map(users.map(user => [user.userName, user])).values()];
      return (
          <ScrollAwareTooltip
              title={
                <div>
                  {distinctUsers.map((user, index) => (
                      <div key={index} className="avatar-box-calendar">
                        <Avatar src={user.userAvatar} className="avatar-calendar" />
                        <Typography variant="subtitle1" color="inherit">
                          {user.userName}
                        </Typography>
                      </div>
                  ))}
                </div>
              }
              arrow
          >
            <Badge
                badgeContent={distinctUsers.length}
                color="primary"
                overlap="circular"
            >
              <Avatar src={distinctUsers[0].userAvatar} className="avatar-badge-calendar" />
            </Badge>
          </ScrollAwareTooltip>
      );
    }

    if (collaborators) {
      return (
          <ScrollAwareTooltip
              title={
                <div>
                  {collaborators.map((collaborator, index) => (
                      <div key={index} className="avatar-box-calendar">
                        <Avatar src={collaborator.image} className="avatar-calendar" />
                        <Typography variant="subtitle1" color="inherit">
                          {collaborator.full_name}
                        </Typography>
                      </div>
                  ))}
                </div>
              }
              arrow
          >
            <div className="flex-content-calendar">
              <Typography variant="subtitle2" className="event-text-calendar">
                {eventInfo.event.title}
              </Typography>
            </div>
          </ScrollAwareTooltip>
      );
    }
    if (affectedUsers && affectedUsers.length > 0) {
      const distinctAffectedUsers = [...new Map(affectedUsers.map(user => [user.full_name, user])).values()];
      return (
          <ScrollAwareTooltip
              title={
                <Box className="affected-users-box-calendar">
                  <Box className="affected-users-box-items-flex-calendar">
                    {eventInfo.event.extendedProps?.country ? (
                        <img
                            src={`https://flagcdn.com/w80/${getCountryFlag(eventInfo.event.extendedProps.country)}.png`}
                            srcSet={`https://flagcdn.com/w80/${getCountryFlag(eventInfo.event.extendedProps.country)}.png 2x`}
                            width="50"
                            height="35"
                            alt="Flag"
                            className="flag-calendar"
                            onError={(e) => { e.target.src = ''; }}
                        />
                    ) : (
                        <TourIcon className="no-country-icon-calendar" />
                    )}
                    <Typography variant="h6" className="holiday-title-calendar">
                      {t("calendar.holiday")}
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="holiday-local-name-calendar">
                    {local_name}
                  </Typography>
                  <Typography variant="subtitle1" className="holiday-name-calendar">
                    ({name})
                  </Typography>
                  <Typography variant="subtitle1" className="holiday-affected-workers-title-calendar">
                    {t("calendar.holiday_workers_affected")}
                  </Typography>
                  {distinctAffectedUsers.map((user, index) => (
                      <Box key={index} className="holiday-affected-workers-avatars-box-calendar">
                        <Avatar src={user.image} className="holiday-affected-workers-avatars-calendar" />
                        <Typography variant="body2" className="holiday-affect-workers-name-calendar">
                          {user.full_name}
                        </Typography>
                      </Box>
                  ))}
                </Box>
              }
              arrow
              PopperProps={{
                className: "holiday-popper-props-calendar"
              }}
          >
            <div className="extended-info-calendar">
              <Typography variant="subtitle2" className="holiday-extended-local-name-calendar">
                {eventInfo.event.extendedProps.local_name}
              </Typography>
              {eventInfo.event.extendedProps.country ? (
                  <img
                      src={`https://flagcdn.com/w80/${getCountryFlag(eventInfo.event.extendedProps.country)}.png`}
                      srcSet={`https://flagcdn.com/w80/${getCountryFlag(eventInfo.event.extendedProps.country)}.png 2x`}
                      width="25"
                      alt="Flag"
                      className="extended-flag-calendar"
                      onError={(e) => { e.target.src = ''; }}
                  />
              ) : (
                  <TourIcon className="holiday-extended-props-icon-calendar" />
              )}
            </div>
          </ScrollAwareTooltip>
      );
    } else {
      return (
          <ScrollAwareTooltip
              title={
                <Box className="affected-users-box-calendar">
                  <Box className="affected-users-box-items-flex-calendar">
                    {country ? (
                        <img
                            src={`https://flagcdn.com/w80/${getCountryFlag(country)}.png`}
                            srcSet={`https://flagcdn.com/w80/${getCountryFlag(country)}.png 2x`}
                            width="50"
                            height="35"
                            alt="Flag"
                            className="flag-calendar"
                        />
                    ) : (
                        <TourIcon className="no-country-icon-calendar" />
                    )}
                    <Typography variant="h6" className="holiday-title-calendar">
                      {t("calendar.holiday")}
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="holiday-local-name-calendar">
                    {local_name}
                  </Typography>
                  <Typography variant="subtitle1" className="holiday-name-calendar">
                    {name}
                  </Typography>
                </Box>
              }
              arrow
              PopperProps={{
                className: "holiday-popper-props-calendar"
              }}
          >
            <div className="flex-content-calendar">
              <Typography variant="subtitle2" className="holiday-extended-local-name-calendar">
                {local_name}
              </Typography>
              {country ? (
                  <img
                      src={`https://flagcdn.com/w80/${getCountryFlag(country)}.png`}
                      srcSet={`https://flagcdn.com/w80/${getCountryFlag(country)}.png 2x`}
                      width="25"
                      alt="Flag"
                      className="extended-flag-calendar"
                  />
              ) : (
                  <TourIcon className="holiday-extended-props-icon-calendar" />
              )}
            </div>
          </ScrollAwareTooltip>
      );
    }

  };

  const handleDatesSet = (dateInfo) => {
    setCurrentView(dateInfo.view.type);
  };


  return (
      <div className="main-content-calendar">
        <Typography
            variant="h4"
            className="title-calendar"
        >
          {t("calendar.workspace_project")}
        </Typography>
        <Box className="checkboxes-calendar">
          <FormControlLabel
              control={<Checkbox checked={showHolidayEvents} onChange={() => setShowHolidayEvents(prev => !prev)} />}
              label={t("calendar.show_holiday_events")}
          />
          {/*currentView !== 'dayGridMonth' && (
          <FormControlLabel
            control={<Checkbox checked={showWorkingHours} onChange={() => setShowWorkingHours(prev => !prev)} />}
            label={t("calendar.show_work_hours")}
          />
        )*/}
          <FormControlLabel
              control={<Checkbox checked={showNormalEvents} onChange={() => setShowNormalEvents(prev => !prev)} />}
              label={t("calendar.show_scheduled_events")}
          />
        </Box>

        <div className="calendar">
          <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, momentPlugin, momentTimezonePlugin]}
              initialView={currentView}
              events={filteredEvents}
              firstDay={calendarStartDay}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              timeZone={myUserTimezone || 'utc'}
              weekends={true}
              editable={false}
              selectable={false}
              eventDisplay={isMobile && currentView === 'dayGridMonth' ? 'none' : 'block'}
              height="auto"
              eventMaxStack={1}

              views={{
                timeGridWeekTwo: {
                  type: 'timeGrid',
                  duration: { days: 2 },
                },
                timeGridWeekThree: {
                  type: 'timeGrid',
                  duration: { days: 3 },
                },
                timeGridWeekFour: {
                  type: 'timeGrid',
                  duration: { days: 4 },
                },
                timeGridWeekFive: {
                  type: 'timeGrid',
                  duration: { days: 5 },
                },
                timeGridWeekSix: {
                  type: 'timeGrid',
                  duration: { days: 6 },
                }
              }}
              eventOrder="country"
              dayCellContent={(arg) => {
                if (arg.view.type === 'dayGridMonth') {
                  const eventsOnDay = arg.view.calendar.getEvents().filter(event => {
                    const dayStart = DateTime.fromJSDate(arg.date).setZone(myUserTimezone).startOf('day');
                    const dayEnd = dayStart.plus({ days: 1 });
                    const eventStart = DateTime.fromJSDate(event.start).setZone(myUserTimezone);

                    const eventEnd = event.end
                        ? DateTime.fromJSDate(event.end).setZone(myUserTimezone)
                        : eventStart.plus({ minutes: 1 });

                    return eventStart < dayEnd && eventEnd > dayStart;
                  });

                  const days = ConvertToMyTimeZone(arg.date.toISOString(), myUserTimezone);
                  const dayNumber = days?.day;

                  const handleDayClick = () => {
                    if (eventsOnDay.length > 0) {
                      openEventModal(eventsOnDay);
                    }
                  };

                  return (
                      <div className="day-cell-content" onClick={handleDayClick}>
                        {dayNumber}
                        {isMobile && eventsOnDay.length > 0 && (
                            <span className="event-count">
            <p className="event-number-calendar">{eventsOnDay.length}</p>
          </span>
                        )}
                      </div>
                  );
                }
                return arg.dayNumberText;
              }}


              eventContent={renderEventContent}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              locale={t("global.lng")}
              buttonText={{
                today: t("calendar.today"),
                day: t("calendar.day"),
                week: t("calendar.week"),
                month: t("calendar.month")
              }}
              dayCellClassNames={(date) => {
                const today = moment().startOf('day');
                const cellDate = moment(date.date).startOf('day');
                return cellDate.isBefore(today) ? 'fc-day-past-calendar past-day-interactive' : '';
              }}
          />
          <EventDetailsModal
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              selectedEvent={selectedEvent}
              myUserPerms={myUserPerms}
              onSave={handleEventSave}
              onDelete={handleEventDelete}
              workspaceId={workspaceId}
              userTimezone={myUserTimezone}
          />

          <Dialog open={mobileDialogOpen} onClose={handleClose} className="modal-background-color modal-mobile-calendar">
            <DialogTitle className="modal-mobile-title-calendar">{t("calendar.event_details")}</DialogTitle>
            <DialogContent className="modal-mobile-content-calendar">
              {selectedEvents.map((event, index) => {
                const distinctAffectedUsers = event.extendedProps.affectedUsers
                    ? [...new Map(event.extendedProps.affectedUsers.map(user => [user.full_name, user])).values()]
                    : [];

                const hasCollaborators = event.extendedProps.collaborators && event.extendedProps.collaborators.length > 0;


                return (
                    <div key={index} className="event-entry-calendar" onClick={hasCollaborators ? () => handleMobileEventClick(event) : null}
                         style={{ cursor: hasCollaborators ? 'pointer' : 'default' }}
                    >
                      <Typography variant="h6">{event.title === "undefined" ? t("calendar.holiday") : event.title}</Typography>

                      <Typography>
                        {t("calendar.start_title")}{" "}
                        {event.allDay
                            ? DateTime.fromISO(event.startStr, { zone: 'utc' }).toISODate()
                            : DateTime.fromISO(event.startStr, { zone: myUserTimezone })
                                .setLocale(t("global.short_lng"))
                                .toLocaleString({ month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </Typography>


                      {hasCollaborators && (
                          <>
                            <Typography>
                              {t("calendar.end_title")}{" "}
                              {event.allDay
                                  ? DateTime.fromISO(event.endStr, { zone: 'utc' }).minus({ days: 1 }).toISODate()
                                  : DateTime.fromISO(event.endStr, { zone: myUserTimezone })
                                      .setLocale(t("global.short_lng"))
                                      .toLocaleString({ month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Typography>
                              {t("calendar.collabs_title")} {event.extendedProps.collaborators.map(collaborator => collaborator.full_name).join(', ')}
                            </Typography>
                          </>
                      )}

                      <hr className="event-divider" />

                      {event.extendedProps.affectedUsers && (
                          <Box className="affected-users-box-calendar">
                            <Box className="affected-users-box-items-flex-calendar">
                              {event.extendedProps.country ? (
                                  <img
                                      src={`https://flagcdn.com/w80/${getCountryFlag(event.extendedProps.country)}.png`}
                                      srcSet={`https://flagcdn.com/w80/${getCountryFlag(event.extendedProps.country)}.png 2x`}
                                      width="50"
                                      height="35"
                                      alt="Flag"
                                      className="flag-calendar"
                                      onError={(e) => { e.target.src = ''; }}
                                  />
                              ) : (
                                  <TourIcon className="no-country-icon-calendar" />
                              )}
                              <Typography variant="h6" className="holiday-title-calendar">
                                {t("calendar.holiday")}
                              </Typography>
                            </Box>

                            <Typography variant="h6" className="holiday-local-name-calendar">
                              {event.extendedProps.local_name}
                            </Typography>
                            <Typography variant="subtitle1" className="holiday-name-calendar">
                              ({event.extendedProps.name})
                            </Typography>

                            <Typography variant="subtitle1" className="holiday-affected-workers-title-calendar">
                              {t("calendar.holiday_workers_affected")}
                            </Typography>
                            {distinctAffectedUsers.map((user, index) => (
                                <Box key={index} className="holiday-affected-workers-avatars-box-calendar">
                                  <Avatar
                                      src={`${user.image}`}
                                      className="holiday-affected-workers-avatars-calendar"
                                  />
                                  <Typography variant="body2" className="holiday-affect-workers-name-calendar">
                                    {user.full_name}
                                  </Typography>
                                </Box>
                            ))}
                          </Box>
                      )}
                    </div>
                );
              })}

            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} className="button-dark-bg">Close</Button>
            </DialogActions>
          </Dialog>




        </div>
      </div>

  );
};

export default CalendarComponent;